import React, { useState, useEffect, useRef } from "react";
import { Button, LogoImg, UtilityModal } from "../";
import { useQuery, useMutation } from "@apollo/client";
import { FETCH_TEMPLATE } from "../../graphql/query/email";
import { UPDATE_CUSTOM_EMAIL_TEMPLATE } from "../../graphql/mutation/emailTemplate";
import { showToastNotification, loadUnlayerScript } from "../../utils";
import MediaLibrary from "../MediaLibrary";
import { IconEdit } from "@tabler/icons";

export interface TemplateData {
  id: string;
  name: string;
  subject?: string;
  preheader?: string;
  designFormat?: string;
  json?: any;
  body?: string;
  tags?: string[];
  siteId?: string;
  position?: number;
}

interface EditTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateData: TemplateData | null;
  onSave: (updatedData: TemplateData) => void;
  loading?: boolean;
}

const EMPTY_DESIGN = { body: { rows: [] }, counters: {} };

const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  isOpen,
  onClose,
  templateData,
  onSave,
  loading = false,
}) => {
  const [editorReadyStatus, setEditorReadyStatus] = useState<Record<string, boolean>>({});
  const [unlayerEditors, setUnlayerEditors] = useState<Record<string, any>>({});
  const [isClosing, setIsClosing] = useState(false);

  
  const [localTemplateData, setLocalTemplateData] = useState<TemplateData | null>(templateData);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(templateData?.name || "");
  const designLoadedRef = useRef(false);

  const [updateTemplate, { loading: updateTemplateLoading }] = useMutation(
    UPDATE_CUSTOM_EMAIL_TEMPLATE
  );

  
  useEffect(() => {
    if (templateData) {
      setLocalTemplateData(templateData);
      setEditedName(templateData.name || "");
      designLoadedRef.current = false;
    }
  }, [templateData]);

  const siteId = templateData?.siteId ? String(templateData.siteId) : null;
  const emailId = templateData?.id ? String(templateData.id) : null;
  const templateId = parseInt(templateData?.id || "0");

  const isValidData =
    isOpen &&
    templateData &&
    siteId &&
    emailId &&
    !isNaN(parseInt(siteId)) &&
    !isNaN(parseInt(emailId)) &&
    parseInt(siteId) > 0 &&
    parseInt(emailId) > 0;

  const { data: { emailTemplate } = {}, loading: templateLoading } = useQuery(
    FETCH_TEMPLATE,
    {
      variables: { id: templateId },
      skip: !isValidData,
      errorPolicy: "all",
      fetchPolicy: "network-only",
    }
  );

  const resolvedJson =
    localTemplateData?.json ||
    emailTemplate?.json ||
    null;
  
  useEffect(() => {
    if (isOpen && templateData && !unlayerEditors[templateData.id]) {
      const initUnlayer = () => {
        const editor = (window as any).unlayer.createEditor({
          id: `html-editor-content-${templateData.id}`,
          displayMode: "email",
          projectId: 1,
          appearance: { panels: { tools: { dock: "left" } } },
        });

        editor.registerCallback("selectImage", (data: any, done: any) => {
          UtilityModal({
            id: "mediaLibrary",
            height: "528px",
            width: "885px",
            body: (props: any) => (
              <MediaLibrary
                siteId={Number(templateData.siteId)}
                handleClose={props.handleClose}
                done={done}
                mediaType="image"
                campaignId={templateData.id}
              />
            ),
          });
        });

        editor.addEventListener("editor:ready", () => {
          setEditorReadyStatus((prev) => ({ ...prev, [templateData.id]: true }));
        });

        setUnlayerEditors((prev) => ({ ...prev, [templateData.id]: editor }));
      };

      loadUnlayerScript({ initUnlayer });
    }

    return () => {
      if (!isOpen && templateData?.id && unlayerEditors[templateData.id]) {
        try {
          unlayerEditors[templateData.id].destroy();
        } catch (err) {
          console.error("Error destroying editor:", err);
        }
        setUnlayerEditors((prev) => {
          const updated = { ...prev };
          delete updated[templateData.id];
          return updated;
        });
        setEditorReadyStatus((prev) => {
          const updated = { ...prev };
          delete updated[templateData.id];
          return updated;
        });
        designLoadedRef.current = false;
      }
    };
  }, [isOpen, templateData, unlayerEditors]);

    
  useEffect(() => {
    if (
      !isOpen ||
      !templateData ||
      !editorReadyStatus[templateData.id] ||
      templateLoading ||
      designLoadedRef.current
    ) {
      return;
    }

    const editor = unlayerEditors[templateData.id];
    if (!editor) return;

    const actualTemplateJson = resolvedJson || EMPTY_DESIGN;

    try {
      editor.loadDesign(actualTemplateJson);
      designLoadedRef.current = true;
    } catch (err) {
      console.error("Error loading design:", err);
      editor.loadDesign(EMPTY_DESIGN);
      designLoadedRef.current = true;
    }
  }, [
    isOpen,
    templateData,
    resolvedJson,
    templateLoading,
    editorReadyStatus,
    unlayerEditors,
  ]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const handleSave = async () => {
    if (!templateData?.id) {
      showToastNotification({
        icon: "alert",
        color: "danger",
        body: "Template data is missing. Cannot save.",
      });
      return;
    }

    let currentJson = resolvedJson || EMPTY_DESIGN;

    if (unlayerEditors[templateData.id]) {
      try {
        await new Promise((resolve) => {
          unlayerEditors[templateData.id].exportHtml((data: any) => {
            if (data?.design) {
              currentJson = data.design;
            }
            resolve(data);
          });
        });
      } catch (error) {
        console.log("Could not export from editor:", error);  
      }
    }

    try {
      const result = await updateTemplate({
        variables: {
          input: {
            id: parseInt(templateData.id),
            name: editedName?.trim() || templateData.name || "Untitled Template",
            subject: templateData.subject || "",
            preheader: templateData.preheader || "",
            json: currentJson,
            tags: [...(templateData.tags || []), "usertemplates"],
            siteIds: [parseInt(templateData.siteId || "0")],
            position: templateData.position || 0,
          },
        },
      });

      if (result.data?.updateEmailTemplate?.success) {
        const updated = {
          ...templateData,
          name: editedName?.trim() || templateData.name,
          json: currentJson,
        };

      
        setLocalTemplateData(updated);
        onSave(updated);

        showToastNotification({
          icon: "check",
          color: "success",
          body: "Template updated successfully!",
        });
      } else {
        showToastNotification({
          icon: "alert",
          color: "danger",
          body: result.data?.updateEmailTemplate?.error || "Update failed",
        });
      }
    } catch (error) {
      console.error("Error saving template:", error);
      showToastNotification({
        icon: "alert",
        color: "danger",
        body: "Error occurred while saving template.",
      });
    }
  };

  if (!isValidData || !isOpen || !templateData) return null;

  const isEditorLoading = !editorReadyStatus[templateData.id] || templateLoading;

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          @keyframes slideDown { from { transform: translateY(0); opacity: 1; } to { transform: translateY(100%); opacity: 0; } }
          .slide-up-animation { animation: slideUp 0.3s forwards; }
          .slide-down-animation { animation: slideDown 0.3s forwards; }
        `,
        }}
      />
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex">
        <div
          className={`flex-1 flex flex-col bg-white h-full ${
            isClosing ? "slide-down-animation" : "slide-up-animation"
          }`}
        >
          
          <div className="flex items-center justify-between p-4 border-b bg-white">
            <div className="flex items-center gap-3">
              <LogoImg logoKey={'square'} />

              {isEditingName ? (
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  className="w-full px-2 py-1 border border-gray-300 text-secondary rounded-md"
                  autoFocus
                />
              ) : (
                <span
                  className="text-lg font-medium text-gray-900 cursor-pointer flex items-center gap-1"
                  onClick={() => setIsEditingName(true)}
                >
                  {editedName?.trim()
                    ? `Editing Template: ${editedName}`
                    : "Editing Template"}
                </span>
              )}

              <button
                onClick={() => setIsEditingName(!isEditingName)}
                className="p-1 rounded hover:bg-gray-100"
                title="Edit Template Name"
              >
                <IconEdit
                  size={18}
                  className="text-blue-500 hover:text-blue-600"
                />
              </button>
            </div>

            <div className="flex items-center gap-3">
              <Button
                type="outline"
                onClick={handleClose}
                label="Close"
              />
              <Button
                onClick={handleSave}
                disabled={updateTemplateLoading || templateLoading}
                label={updateTemplateLoading ? "SAVING..." : "SAVE"}
              />
            </div>
          </div>

          
          <div className="flex-1 overflow-hidden">
            <div className="h-full w-full flex flex-col">
              {isEditorLoading && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">
                      {templateLoading ? "Loading template..." : "Loading editor..."}
                    </p>
                  </div>
                </div>
              )}
              <div
                id={`html-editor-content-${templateData.id}`}
                className={`h-full w-full ${
                  isEditorLoading ? "hidden" : "block"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditTemplateModal;
